import { useQueryClient } from '@tanstack/react-query';
import { useAppMutation } from '@/api/hooks/useAppMutation';
import {
  createAnalysis,
  triggerAnalysis,
  deleteAnalysis,
  checkAnalysisNameAvailability
} from './api';
import {
  AnalysisCreateRequest,
  AnalysisTriggerRequest,
  AnalysisDetail,
  AnalysisTriggerResponse
} from './types';
import { analysisKeys } from './queries';

/**
 * Create new analysis
 */
export function useCreateAnalysis() {
  return useAppMutation<AnalysisDetail, AnalysisCreateRequest>({
    mutationFn: createAnalysis,
    successMessage: 'Analysis created successfully',
    showSuccessToast: true
  });
}

/**
 * Trigger analysis execution
 */
export function useTriggerAnalysis() {
  return useAppMutation<
    AnalysisTriggerResponse,
    { analysisId: string; config?: AnalysisTriggerRequest }
  >({
    mutationFn: ({ analysisId, config }) => triggerAnalysis(analysisId, config)
  });
}

/**
 * Delete analysis
 */
export function useDeleteAnalysis() {
  const queryClient = useQueryClient();

  return useAppMutation<void, string>({
    mutationFn: deleteAnalysis,
    successMessage: 'Analysis deleted',
    showSuccessToast: true,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: analysisKeys.lists() });
    }
  });
}

/**
 * Check availability of analysis name
 */
export function useCheckAnalysisNameAvailability() {
  return useAppMutation<boolean, string>({
    mutationFn: checkAnalysisNameAvailability
  });
}
